import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Input from "../ui/Input";
import Select from "../ui/Select";
import { tl } from "../../utils/format";
import { useHistory } from "react-router-dom";
import AuthContext from "../../Context/AuthContext";
import authHeader from "../../utils/authHeader";
// import data2 from "../../data/lcwkonkur.json";
// import magazalar from "../../data/lcwmagaza.json";
import Map from "../results/mapSahibinden";

export default function NewPlanPage({
  data = { advertisers: [] },
  initialBrandId = null,
  onCancel,
  onSaved,
  saveUrl = "https://opt-api.vercel.app/NewPlan",
}) {
  // Excel verisi için state
  const [excelData, setExcelData] = useState([]);
  const [loadingExcel, setLoadingExcel] = useState(true);
  const [successPopup, setSuccessPopup] = useState(false);

  // AI Hedef Kitle Analizi state'leri
  const [aiQuery, setAiQuery] = useState("");
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiPopup, setAiPopup] = useState(false);
  const [aiSelectedLocations, setAiSelectedLocations] = useState([]);


  // Excel dosyasını yükle
  useEffect(() => {
    const loadExcel = async () => {
      try {
        const response = await fetch("/data/my.xlsx");
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        setExcelData(jsonData);
      } catch (error) {
        console.error("Excel dosyası yüklenirken hata:", error);
      } finally {
        setLoadingExcel(false);
      }
    };
    loadExcel();
  }, []);

  // Benzersiz ünite listesi
  const unitOptions = useMemo(() => {
    const units = [...new Set(excelData.map((row) => row["Ünite"]).filter(Boolean))];
    return ["", ...units];
  }, [excelData]);

  // Üniteye göre adres listesi
  const getAddressesByUnit = (unit) => {
    if (!unit) return [""];
    const addresses = excelData
      .filter((row) => row["Ünite"] === unit)
      .map((row) => row["Adres"])
      .filter(Boolean);
    return ["", ...addresses];
  };

  // Adrese göre detayları getir
  const getDetailsByAddress = (address) => {
    const row = excelData.find((r) => r["Adres"] === address);
    if (!row) return { unitNumber: 0, faces: 0, unitPrice: 0 };
    return {
      unitNumber: Number(row["Ünite Sayısı"]) || 0,
      faces: Number(row["Face"]) || 0,
      unitPrice: Number(row["Fiyat"]) || 0,
    };
  };

  // markalar
  const brandOptions = useMemo(() => {
    const out = [];
    (data.advertisers || []).forEach((adv) =>
      (adv.brands || []).forEach((b) =>
        out.push({ brandId: b.id, label: `${adv.name} / ${b.name}` })
      )
    );
    return out;
  }, [data]);

  const makeEmptyRow = () => ({
    id: String(Date.now()) + Math.random().toString(36).slice(2, 8),
    selectedUnit: "",
    address: "",
    startDate: "",
    endDate: "",
    unit: "",
    unitNumber: 0,
    faces: 0,
    unitPrice: 0,
    vendor: "",
  });

  const [brandId, setBrandId] = useState(() => initialBrandId || brandOptions[0]?.brandId || "");
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [saving, setSaving] = useState(false);
  const history = useHistory();
  const { userData } = useContext(AuthContext);

  // Sadece AI analizi ile seçilen lokasyonları haritada göster
  const combinedData = useMemo(() => {
    return (Array.isArray(aiSelectedLocations) ? aiSelectedLocations : [])
      .filter((loc) => loc.latitude && loc.longitude)
      .map((loc) => ({
        ...loc,
        Adres: loc["Adres"] || "",
        "Liste Tür": loc["Ünite"] || "AI Öneri",
      }));
  }, [aiSelectedLocations]);

  useEffect(() => {
    if (!brandId && brandOptions[0]) setBrandId(brandOptions[0].brandId);
  }, [brandOptions, brandId]);

  // hesap: satır bütçesi ve toplam (Excel'deki fiyat direkt budget olarak kullanılıyor)
  const rowBudget = (r) => Number(r.unitPrice) || 0;
  const totals = rows.reduce((acc, r) => acc + rowBudget(r), 0);

  // satır işlemleri
  const updateRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, makeEmptyRow()]);
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  // Ünite değiştiğinde
  const handleUnitChange = (rowId, newUnit) => {
    updateRow(rowId, {
      selectedUnit: newUnit,
      address: "",
      unitNumber: 0,
      faces: 0,
      unitPrice: 0,
    });
  };

  // Adres değiştiğinde otomatik doldur
  const handleAddressChange = (rowId, newAddress) => {
    const details = getDetailsByAddress(newAddress);
    updateRow(rowId, {
      address: newAddress,
      unitNumber: details.unitNumber,
      faces: details.faces,
      unitPrice: details.unitPrice,
    });
  };

  // AI Hedef Kitle Analizi
  const handleAiAnalysis = () => {
    if (!aiQuery.trim()) return;
    setAiAnalyzing(true);

    // Yapay zeka çalışıyormuş gibi 2-3 saniye beklet
    setTimeout(() => {
      // excelData'dan rastgele 4-5 lokasyon seç
      const count = Math.random() < 0.5 ? 4 : 5;
      const shuffled = [...excelData].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, Math.min(count, shuffled.length));

      // Excel'deki Lat/Lng sütunlarını kullan
      const withCoords = selected.map((loc) => ({
        ...loc,
        latitude: loc["Lat"] || loc["latitude"] || loc["LAT"],
        longitude: loc["Lng"] || loc["longitude"] || loc["LNG"],
      }));

      setAiSelectedLocations(withCoords);

      // Seçilen lokasyonları plan satırlarına ekle
      const newRows = selected.map((loc) => ({
        id: String(Date.now()) + Math.random().toString(36).slice(2, 8),
        selectedUnit: loc["Ünite"] || "",
        address: loc["Adres"] || "",
        startDate: "",
        endDate: "",
        unit: loc["Ünite"] || "",
        unitNumber: Number(loc["Ünite Sayısı"]) || 0,
        faces: Number(loc["Face"]) || 0,
        unitPrice: Number(loc["Fiyat"]) || 0,
        vendor: "",
      }));

      setRows((prev) => [...prev.filter((r) => r.address !== ""), ...newRows]);
      setAiAnalyzing(false);
      setAiPopup(true);

      // Popup'ı 3 saniye sonra kapat
      setTimeout(() => setAiPopup(false), 3000);
    }, 2500);
  };

  // notlar
  const addNote = () => {
    const t = noteDraft.trim();
    if (!t) return;
    setNotes((n) => [...n, t]);
    setNoteDraft("");
  };
  const removeNote = (i) => setNotes((n) => n.filter((_, idx) => idx !== i));

  // doğrulama
  const validate = () => {
    const errs = [];
    if (!name.trim()) errs.push("Plan adı (Campaign) gerekli.");
    rows.forEach((r, i) => {
      if (!r.address) errs.push(`Satır ${i + 1}: Address gerekli`);
      if (!r.startDate) errs.push(`Satır ${i + 1}: Start Date gerekli`);
      if (!r.endDate) errs.push(`Satır ${i + 1}: End Date gerekli`);
      if (r.startDate && r.endDate && new Date(r.endDate) < new Date(r.startDate)) {
        errs.push(`Satır ${i + 1}: End Date, Start Date'ten önce olamaz`);
      }
    });
    return errs;
  };

  // payload
  const buildPayload = () => ({
    name,
    brandId: userData.user.user.username,
    currency,
    notes: notes.join("\n"),
    addresses: rows.map((r) => ({
      address: r.address,
      startDate: r.startDate ? new Date(r.startDate) : undefined,
      endDate: r.endDate ? new Date(r.endDate) : undefined,
      period: 1,
      unit: r.selectedUnit,
      unitNumber: Number(r.unitNumber) || 0,
      faces: Number(r.faces) || 0,
      unitPrice: Number(r.unitPrice) || 0,
      vendor: r.vendor,
    })),
  });

  // kaydet
  const handleSave = async () => {
    const errs = validate();
    if (errs.length) {
      alert("Lütfen hataları düzeltin:\n\n" + errs.join("\n"));
      return;
    }
    const payload = buildPayload();
    setSaving(true);
    try {
      const res = await axios.post(saveUrl, payload, authHeader());
      onSaved?.(res.data);
      setSuccessPopup(true);

      setTimeout(() => {
        history.push("/homepage");
      }, 2000);

    } catch (err) {
      console.error(err);
      alert("Kaydetme sırasında hata: " + (err.response?.data?.hata || err.message || err));
    } finally {
      setSaving(false);
    }
  };

  if (loadingExcel) {
    return <div className="p-4">Excel verisi yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 bg-white p-4 rounded shadow-sm">
        <button onClick={() => history.push("/homepage")} className="px-3 py-1 rounded border text-sm">
          ‹ Geri
        </button>
        <div className="ms-auto flex items-center gap-3">
          <div className="text-sm text-slate-600">
            Toplam: <span className="font-medium">{tl(totals)}</span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-4 rounded bg-brand-600 text-white hover:bg-brand-700"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="card p-4 bg-white rounded shadow-sm">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block text-sm">
            <div className="text-slate-600 mb-1">Kampanya İsmi</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: İstanbul Kasım Outdoor" />
          </label>
        </div>
      </div>

      {/* AI Hedef Kitle Analizi */}
      <div className="card p-4 bg-white rounded shadow-sm">
        <div className="font-medium mb-3">AI Hedef Kitle Analizi</div>
        <div className="flex gap-3 items-end">
          <label className="block text-sm flex-1">
            <div className="text-slate-600 mb-1">Hedef Kitlenizi Tanımlayın</div>
            <textarea
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              placeholder="Örn: 25-35 yaş arası, orta-üst gelir grubu, spor ve teknoloji ile ilgilenen erkek hedef kitle..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              rows={3}
            />
          </label>
          <button
            onClick={handleAiAnalysis}
            disabled={aiAnalyzing || !aiQuery.trim()}
            className="h-10 px-5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            {aiAnalyzing ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Analiz Ediliyor...
              </>
            ) : (
              "Analiz Et"
            )}
          </button>
        </div>
        {aiAnalyzing && (
          <div className="mt-3 p-3 bg-indigo-50 rounded-lg text-sm text-indigo-700">
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              Yapay zeka hedef kitlenize uygun lokasyonları analiz ediyor...
            </div>
          </div>
        )}
      </div>

      {/* Satırlar */}
      <div className="card p-0 overflow-hidden bg-white rounded shadow-sm">
        <div className="p-3 flex items-center justify-between">
          <div className="font-medium">Satırlar</div>
          <button onClick={addRow} className="h-9 px-3 rounded bg-emerald-600 text-white">
            + Satır Ekle
          </button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead>
              <tr className="text-slate-600 bg-slate-50">
                {["Ünite", "Adres", "Başlangıç Tarihi", "Bitiş Tarihi", "Ünite Sayısı", "Face", "Fiyat", "Vendor", ""].map(
                  (h) => (
                    <th key={h} className="p-2 text-left">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} className={idx % 2 ? "bg-slate-50/50" : ""}>
                  <td className="p-2">
                    <Select value={r.selectedUnit} onChange={(e) => handleUnitChange(r.id, e.target.value)}>
                      {unitOptions.map((u) => (
                        <option key={u} value={u}>
                          {u || "Ünite seçiniz"}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="p-2">
                    <Select
                      value={r.address}
                      onChange={(e) => handleAddressChange(r.id, e.target.value)}
                      disabled={!r.selectedUnit}
                    >
                      {getAddressesByUnit(r.selectedUnit).map((a) => (
                        <option key={a} value={a}>
                          {a || "Adres seçiniz"}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="p-2">
                    <Input type="date" value={r.startDate} onChange={(e) => updateRow(r.id, { startDate: e.target.value })} />
                  </td>
                  <td className="p-2">
                    <Input type="date" value={r.endDate} onChange={(e) => updateRow(r.id, { endDate: e.target.value })} />
                  </td>
                  <td className="p-2">
                    <span className="px-2 py-1 bg-slate-100 rounded">{r.unitNumber}</span>
                  </td>
                  <td className="p-2">
                    <span className="px-2 py-1 bg-slate-100 rounded">{r.faces}</span>
                  </td>
                  <td className="p-2 font-medium">{tl(rowBudget(r))}</td>
                  <td className="p-2">
                    <Input value={r.vendor} onChange={(e) => updateRow(r.id, { vendor: e.target.value })} placeholder="Vendor" />
                  </td>
                  <td className="p-2">
                    <button onClick={() => removeRow(r.id)} className="text-rose-600">
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td colSpan={6} className="text-right p-2 font-semibold">
                  Total
                </td>
                <td className="p-2 font-semibold">{tl(totals)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
          <Map rawData={combinedData} zoom={12} />
        </div>
      </div>
      {/* AI Analiz Sonucu Popup */}
      {aiPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 w-[420px] text-center animate-scale-in shadow-2xl">
            <div className="w-14 h-14 mx-auto mb-4 bg-indigo-100 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-indigo-600">
              AI Analiz Tamamlandı
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              Hedef kitle analizine uygun <strong>{aiSelectedLocations.length} lokasyon</strong> belirlendi ve planınıza otomatik olarak eklendi.
            </p>
            <div className="mt-4 text-left max-h-40 overflow-auto">
              {aiSelectedLocations.map((loc, i) => (
                <div key={i} className="text-xs text-slate-600 py-1 border-b border-slate-100 flex items-center gap-2">
                  <span className="w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                  <span>{loc["Adres"] || loc["Ünite"] || `Lokasyon ${i + 1}`}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setAiPopup(false)}
              className="mt-5 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            >
              Tamam
            </button>
          </div>
        </div>
      )}

      {successPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 w-[360px] text-center animate-scale-in shadow-2xl">

            {/* <div className="text-6xl mb-4 animate-bounce">🎉</div> */}

            <h2 className="text-xl font-semibold text-emerald-600">
              Plan Başarıyla Kaydedildi
            </h2>

            <p className="text-slate-500 text-sm mt-2">
              Ana sayfaya yönlendiriliyorsunuz...
            </p>

            <div className="mt-5 h-1 w-full bg-slate-100 rounded overflow-hidden">
              <div className="h-full bg-emerald-500 animate-progress"></div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
